// Inicio requerimiento PLUS-0097-H5 Samunoz
import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { NotificacionService } from '@core/services/notificacion.service';
import { PreguntasSeguridadService } from '@core/services/auth/preguntas-seguridad.service';
import {
    PreguntaSeguridadItem,
    RespuestaPreguntaSeguridadItem
} from '@features/authentication/models/preguntas-seguridad.interface';
import { CodigosRespuestaSIAFPConstants } from '@shared/constants/codigos-respuesta-siafp-constants';
import { TypeAlertEnum } from '@shared/enums/type.alert.enum';
import { encrypt } from '@shared/util/util-encrypt-rsa';

/** Resultado al cerrar el modal. */
export interface PreguntasSeguridadModalResult {
    confirmed: boolean;
    preguntas?: RespuestaPreguntaSeguridadItem[];
    /**
     * True si el modal ya persistio (Flujos 3 y 4).
     * Evita un segundo POST en el gate.
     */
    guardado?: boolean;
}

/**
 * Datos de apertura del modal.
 * - crearContrasena: Flujo 1 (banner post-password).
 * - registro: Flujo 3 (primera vez, sin banner).
 * - edicion: Flujo 4 (ya tiene preguntas; respuesta vacia conserva el hash previo).
 */
export interface PreguntasSeguridadModalData {
    modo?: 'crearContrasena' | 'registro' | 'edicion';
    preguntasIniciales?: PreguntaSeguridadItem[];
    cantidadRequerida?: number;
    /**
     * Catalogo ya obtenido (Flujo 1 via validarRecuperacionUsuario).
     * Si viene poblado, no se llama consultarCatalogo (evita Bearer / redirect).
     */
    catalogo?: PreguntaSeguridadItem[];
}

/** Item anadido por el usuario en el modal. */
interface PreguntaAnadida {
    codigo: string;
    pregunta: string;
    respuesta: string;
}

/**
 * Modal reutilizable para registrar o editar preguntas de seguridad.
 * Flujo 1: solo recolecta (el padre persiste con la contrasena nueva).
 * Flujos 3 y 4: persiste aqui con reauth; si la contrasena falla, el modal permanece abierto.
 */
@Component({
    selector: 'app-preguntas-seguridad-modal',
    standalone: true,
    imports: [FormsModule, MatDialogModule],
    templateUrl: './preguntas-seguridad-modal.component.html',
    styleUrl: './preguntas-seguridad-modal.component.scss'
})
export class PreguntasSeguridadModalComponent implements OnInit {

    private static readonly DURACION_TOAST_MS = 3000;

    private readonly _dialogRef = inject(MatDialogRef<PreguntasSeguridadModalComponent, PreguntasSeguridadModalResult>);
    private readonly _preguntasService = inject(PreguntasSeguridadService);
    private readonly _notificacionService = inject(NotificacionService);
    private readonly _data = inject<PreguntasSeguridadModalData | null>(MAT_DIALOG_DATA, { optional: true });

    public cargandoCatalogo = true;
    public finalizando = false;
    public errorCatalogo: string | null = null;
    public cantidadRequerida = 3;
    public catalogo: PreguntaSeguridadItem[] = [];
    public anadidas: PreguntaAnadida[] = [];
    public codigoSeleccionado = '';
    public respuestaActual = '';
    public intentoAnadir = false;
    public intentoFinalizar = false;
    public contrasenaActual = '';
    public ocultarContrasena = true;

    /** Codigos precargados en edicion; respuesta vacia = conservar hash en back. */
    private readonly _codigosIniciales = new Set<string>();

    /** Modo de UI segun el flujo que abrio el modal. */
    public get modo(): 'crearContrasena' | 'registro' | 'edicion' {
        return this._data?.modo ?? 'registro';
    }

    /** True si es edicion desde perfil (Flujo 4). */
    public get esEdicion(): boolean {
        return this.modo === 'edicion';
    }

    /** Flujos 3 y 4: reauth con contrasena de sesion. */
    public get requiereContrasena(): boolean {
        return this.modo !== 'crearContrasena';
    }

    /**
     * Preguntas del catalogo que aun no fueron elegidas.
     */
    public get disponibles(): PreguntaSeguridadItem[] {
        const usados = new Set(this.anadidas.map((a) => a.codigo));
        return this.catalogo.filter((p) => !usados.has(p.codigo));
    }

    /**
     * Indica si ya se alcanzaron las preguntas requeridas con respuestas validas
     * y, si aplica, contrasena de reauth.
     * En edicion, respuesta vacia es valida solo si el codigo ya estaba registrado.
     */
    public get completo(): boolean {
        if (this.anadidas.length < this.cantidadRequerida) {
            return false;
        }
        const preguntasOk = this.anadidas.every((a) => this.esRespuestaEditableValida(a));
        if (!preguntasOk) {
            return false;
        }
        if (this.requiereContrasena && !(this.contrasenaActual ?? '').trim()) {
            return false;
        }
        return true;
    }

    /**
     * Cantidad de preguntas que faltan por anadir.
     */
    public get restantes(): number {
        return Math.max(this.cantidadRequerida - this.anadidas.length, 0);
    }

    ngOnInit(): void {
        const catalogoPrevio = this._data?.catalogo;
        if (catalogoPrevio?.length) {
            this.cargandoCatalogo = false;
            this.catalogo = catalogoPrevio;
            this.cantidadRequerida = this._data?.cantidadRequerida ?? 3;
            this.precargarPreguntasIniciales();
            return;
        }
        this._preguntasService.consultarCatalogo().subscribe({
            next: (response) => {
                this.cargandoCatalogo = false;
                if (response?.codigoRespuesta === CodigosRespuestaSIAFPConstants.RESPUESTA_EXITOSA
                    && response.preguntas?.length) {
                    this.catalogo = response.preguntas;
                    this.cantidadRequerida = this._data?.cantidadRequerida
                        ?? response.cantidadRequerida
                        ?? 3;
                    this.precargarPreguntasIniciales();
                    return;
                }
                this.errorCatalogo = response?.descripcionRespuesta
                    || 'No fue posible cargar las preguntas de seguridad.';
            },
            error: () => {
                this.cargandoCatalogo = false;
                this.errorCatalogo = 'Ocurrio un error tecnico al cargar las preguntas.';
            }
        });
    }

    /**
     * Anade la pregunta seleccionada a la lista temporal.
     */
    public anadir(): void {
        this.intentoAnadir = true;
        const respuesta = (this.respuestaActual ?? '').trim();
        if (!this.codigoSeleccionado || !this.esRespuestaValida(respuesta)) {
            return;
        }
        const item = this.catalogo.find((p) => p.codigo === this.codigoSeleccionado);
        if (!item || this.anadidas.length >= this.cantidadRequerida) {
            return;
        }
        this.anadidas = [...this.anadidas, {
            codigo: item.codigo,
            pregunta: item.pregunta,
            respuesta
        }];
        this.codigoSeleccionado = '';
        this.respuestaActual = '';
        this.intentoAnadir = false;
        this.intentoFinalizar = false;
    }

    /**
     * Quita una pregunta de la lista temporal.
     *
     * @param codigo identificador de la pregunta en el catalogo
     */
    public quitar(codigo: string): void {
        this.anadidas = this.anadidas.filter((a) => a.codigo !== codigo);
    }

    /**
     * Confirma el formulario.
     * Sin reauth (Flujo 1): cierra y entrega las preguntas al padre.
     * Con reauth (Flujos 3 y 4): persiste; solo cierra si el guardado es exitoso.
     */
    public completar(): void {
        this.intentoFinalizar = true;
        if (!this.completo) {
            return;
        }
        const preguntas: RespuestaPreguntaSeguridadItem[] = this.anadidas.map((a) => ({
            codigo: a.codigo,
            respuesta: (a.respuesta ?? '').trim()
        }));
        if (!this.requiereContrasena) {
            this.finalizando = true;
            this._dialogRef.close({ confirmed: true, preguntas });
            return;
        }
        this.guardarConReauth(preguntas);
    }

    /**
     * Persiste con contrasena RSA. Si falla, mantiene el modal y las respuestas.
     *
     * @param preguntas codigo y respuesta en claro
     */
    private guardarConReauth(preguntas: RespuestaPreguntaSeguridadItem[]): void {
        this.finalizando = true;
        const contrasenaCifrada = encrypt((this.contrasenaActual ?? '').trim());
        if (!contrasenaCifrada) {
            this.manejarErrorGuardado('No fue posible cifrar la contrasena. Intente de nuevo.');
            return;
        }
        this._preguntasService.guardar({
            preguntas,
            contrasena: contrasenaCifrada
        }).subscribe({
            next: (response) => {
                if (response?.codigoRespuesta === CodigosRespuestaSIAFPConstants.RESPUESTA_EXITOSA) {
                    this._dialogRef.close({ confirmed: true, preguntas, guardado: true });
                    return;
                }
                this.manejarErrorGuardado(
                    response?.descripcionRespuesta
                    || 'No fue posible guardar las preguntas de seguridad.'
                );
            },
            error: (err: HttpErrorResponse) => {
                const descripcion = err?.error?.descripcionRespuesta;
                this.manejarErrorGuardado(
                    (typeof descripcion === 'string' && descripcion.trim())
                        ? descripcion
                        : 'Ocurrio un error tecnico al guardar las preguntas. Intente de nuevo.'
                );
            }
        });
    }

    /**
     * Deja el modal abierto tras un fallo de guardado (p. ej. contrasena invalida).
     *
     * @param mensaje texto a mostrar en el toast
     */
    private manejarErrorGuardado(mensaje: string): void {
        this.finalizando = false;
        this.contrasenaActual = '';
        this._notificacionService.mostrarNotificacionBasica(
            mensaje,
            TypeAlertEnum.ERROR,
            PreguntasSeguridadModalComponent.DURACION_TOAST_MS
        );
    }

    /**
     * Indica error de seleccion de pregunta al intentar anadir.
     */
    public get errorSeleccion(): boolean {
        return this.intentoAnadir && !this.codigoSeleccionado;
    }

    /**
     * Indica error de formato de respuesta al intentar anadir.
     */
    public get errorRespuesta(): boolean {
        if (!this.intentoAnadir) {
            return false;
        }
        return !this.esRespuestaValida(this.respuestaActual);
    }

    /**
     * Indica si alguna respuesta precargada/editada es invalida al finalizar.
     */
    public get errorRespuestasLista(): boolean {
        return this.intentoFinalizar
            && this.anadidas.length >= this.cantidadRequerida
            && this.anadidas.some((a) => !this.esRespuestaEditableValida(a));
    }

    /**
     * Indica si falta la contrasena de reauth al finalizar.
     */
    public get errorContrasena(): boolean {
        return this.intentoFinalizar
            && this.requiereContrasena
            && !(this.contrasenaActual ?? '').trim();
    }

    public toggleVisibilidadContrasena(): void {
        this.ocultarContrasena = !this.ocultarContrasena;
    }

    private precargarPreguntasIniciales(): void {
        const iniciales = this._data?.preguntasIniciales;
        if (!iniciales?.length) {
            return;
        }
        this._codigosIniciales.clear();
        this.anadidas = iniciales.slice(0, this.cantidadRequerida).map((p) => {
            this._codigosIniciales.add(p.codigo);
            return {
                codigo: p.codigo,
                pregunta: p.pregunta,
                respuesta: ''
            };
        });
    }

    /**
     * En edicion permite vacio si el codigo ya existia; si hay texto, aplica reglas.
     *
     * @param item pregunta de la lista
     * @return true si el campo es aceptable para guardar
     */
    public esRespuestaEditableOk(item: { codigo: string; respuesta: string }): boolean {
        return this.esRespuestaEditableValida(item);
    }

    /**
     * En edicion permite vacio si el codigo ya existia; si hay texto, aplica reglas.
     */
    private esRespuestaEditableValida(item: { codigo: string; respuesta: string }): boolean {
        const respuesta = (item.respuesta ?? '').trim();
        if (!respuesta) {
            return this.esEdicion && this._codigosIniciales.has(item.codigo);
        }
        return this.esRespuestaValida(respuesta);
    }

    private esRespuestaValida(cadena: string | null | undefined): boolean {
        const respuesta = (cadena ?? '').trim();
        return respuesta.length >= 2
            && respuesta.length <= 20
            && !this.contieneCaracterSospechoso(respuesta);
    }

    /**
     * Replica la validacion de caracteres del portal viejo.
     *
     * @param cadena respuesta en claro
     * @returns true si contiene caracter no permitido
     */
    private contieneCaracterSospechoso(cadena: string): boolean {
        return /[-'=]/.test(cadena);
    }
}
// Fin requerimiento PLUS-0097-H5 Samunoz
